import * as THREE from 'three/webgpu';
import { SketchRelationKind } from '../solver/constraint/relation-compatibility/SketchRelationKinds';
import {
    isSketchLineObject,
    isSketchPointObject,
} from '../../mesh/guards';
import {
    SketchCollectionCreationSource,
    type SketchCollectionObject,
} from '../../mesh/sketch/SketchCollectionObject';
import type { ISketchObject } from '../../mesh/sketch/ISketchObject';
import type { SketchLineObject } from '../../mesh/sketch/SketchLineObject';
import type { SketchPointObject } from '../../mesh/sketch/SketchPointObject';

//------ 矩形构造线归属解析 开始------
/**
 * 矩形构造线来源字面量。
 */
const RECTANGLE_CONSTRUCTION_LINE_MODE = {
    /** 从矩形四个角点生成两条对角构造线。 */
    corners: 'corners',
    /** 从矩形相对边中点生成两条中心构造线。 */
    midpoints: 'midpoints',
} as const;

/**
 * 矩形构造线来源类型。
 */
type RectangleConstructionLineMode =
    typeof RECTANGLE_CONSTRUCTION_LINE_MODE[keyof typeof RECTANGLE_CONSTRUCTION_LINE_MODE];

/**
 * 轮廓拖拽矩形构造线解析结果。
 */
export interface RectangleContourDragConstructionGeometry {
    /** 应随轮廓拖拽同步的构造线。 */
    lines: SketchLineObject[];
    /** 应随轮廓拖拽同步的构造线辅助点。 */
    points: SketchPointObject[];
}

/**
 * 矩形构造线几何数据片段。
 */
interface SketchRectangleConstructionLineGeometryData {
    /** 矩形绘制参数片段。 */
    rectangle?: {
        /** 矩形构造线来源。 */
        constructionLines?: unknown;
    } | null;
    /** 矩形构造线数据。 */
    constructionLines?: {
        /** 构造线对象 ID 列表。 */
        lineIds?: readonly unknown[];
        /** 构造线辅助点对象 ID 列表。 */
        pointIds?: readonly unknown[];
    };
}

/**
 * 矩形构造线归属解析服务。
 *
 * 设计说明：
 * 1. 矩形外框是 `SketchCollectionObject.collectSet`，但附加构造线按真实草图实体挂在草图组下；
 * 2. 二者的归属关系通过矩形集合 `geometryData.constructionLines` 记录；
 * 3. 移动实体、轮廓拖动等集合级交互必须复用同一解析入口，避免某个交互路径漏同步辅助对象。
 */
export class SketchRectangleConstructionLineResolver {
    /**
     * 复制集合几何数据，并把矩形辅助对象引用重映射到当前副本域。
     *
     * 设计说明：
     * 1. `constructionLines.lineIds/pointIds` 保存的是运行时 UUID，不能直接跨复制域继承；
     * 2. 只有当前复制上下文中真实存在的构造线和中心点才能进入副本引用；
     * 3. 未被复制的辅助对象必须从副本元数据中移除，禁止回退引用源矩形对象。
     *
     * @param sourceCollection 源集合对象。
     * @param objectMap 源对象 UUID 到副本对象的映射。
     * @returns 与源数据解耦且已完成辅助对象重映射的几何数据。
     */
    public cloneGeometryDataForCopiedCollection(
        sourceCollection: SketchCollectionObject,
        objectMap: ReadonlyMap<string, ISketchObject>,
    ): Record<string, any> | null {
        if (!sourceCollection.geometryData) {
            return null;
        }

        const copiedGeometryData = structuredClone(
            sourceCollection.geometryData,
        ) as SketchRectangleConstructionLineGeometryData;
        if (sourceCollection.creationSource !== SketchCollectionCreationSource.Rectangle) {
            return copiedGeometryData as Record<string, any>;
        }

        const constructionLines = copiedGeometryData.constructionLines;
        if (!constructionLines) {
            return copiedGeometryData as Record<string, any>;
        }

        constructionLines.lineIds = this.remapCopiedObjectIds(
            constructionLines.lineIds,
            objectMap,
            isSketchLineObject,
        );
        constructionLines.pointIds = this.remapCopiedObjectIds(
            constructionLines.pointIds,
            objectMap,
            isSketchPointObject,
        );
        return copiedGeometryData as Record<string, any>;
    }

    /**
     * 解析矩形几何数据中记录的构造线对象。
     *
     * @param collection 集合对象。
     * @returns 矩形构造线对象列表。
     */
    public resolveConstructionLines(collection: SketchCollectionObject): SketchLineObject[] {
        const lineIds = this.resolveConstructionLineIds(collection);
        if (lineIds.length === 0) {
            return [];
        }

        const searchRoot = this.resolveSearchRoot(collection);
        return lineIds
            .map((lineId) => searchRoot.getObjectByProperty('uuid', lineId))
            .filter((object): object is SketchLineObject => !!object && isSketchLineObject(object));
    }

    /**
     * 解析轮廓拖拽中应跟随矩形外框的构造线和辅助点。
     *
     * 规则说明：
     * 1. `From Corners` 构造线端点复用矩形角点，属于轮廓刚体平移的一部分；
     * 2. `From Midpoints` 构造线端点默认是独立点，只有存在自动创建的 `Midpoint` 关系时才表达“跟随外框边中点”的设计意图；
     * 3. 这里按真实几何关系判断，而不读取当前 UI 开关，避免后续关闭自动几何关系后破坏已创建关系的矩形。
     *
     * @param collection 矩形集合对象。
     * @returns 轮廓拖拽应同步的构造线和辅助点。
     */
    public resolveContourDragConstructionGeometry(
        collection: SketchCollectionObject,
    ): RectangleContourDragConstructionGeometry {
        const lines = this.resolveConstructionLines(collection);
        if (!this.shouldConstructionGeometryFollowContourDrag(collection, lines)) {
            return {
                lines: [],
                points: [],
            };
        }

        return {
            lines,
            points: this.resolveConstructionPoints(collection),
        };
    }

    /**
     * 解析矩形几何数据中记录的构造线辅助点对象。
     *
     * @param collection 集合对象。
     * @returns 矩形构造线辅助点对象列表。
     */
    public resolveConstructionPoints(collection: SketchCollectionObject): SketchPointObject[] {
        const pointIds = this.resolveConstructionPointIds(collection);
        if (pointIds.length === 0) {
            return [];
        }

        const searchRoot = this.resolveSearchRoot(collection);
        return pointIds
            .map((pointId) => searchRoot.getObjectByProperty('uuid', pointId))
            .filter((object): object is SketchPointObject => !!object && isSketchPointObject(object));
    }

    /**
     * 解析对象查找根节点。
     *
     * @param collection 集合对象。
     * @returns 构造线 ID 查找根节点。
     */
    private resolveSearchRoot(collection: SketchCollectionObject): THREE.Object3D {
        // 矩形构造线正式创建后与矩形集合同级挂在草图组下，因此优先从父节点搜索。
        return collection.parent ?? collection;
    }

    /**
     * 判断构造线是否应跟随轮廓拖拽。
     *
     * @param collection 矩形集合对象。
     * @param lines 已解析的构造线对象。
     * @returns 应跟随返回 true。
     */
    private shouldConstructionGeometryFollowContourDrag(
        collection: SketchCollectionObject,
        lines: readonly SketchLineObject[],
    ): boolean {
        if (lines.length === 0) {
            return false;
        }

        const mode = this.resolveConstructionLineMode(collection);
        if (mode === RECTANGLE_CONSTRUCTION_LINE_MODE.corners) {
            return true;
        }

        if (mode === RECTANGLE_CONSTRUCTION_LINE_MODE.midpoints) {
            return this.hasMidpointRelation(lines);
        }

        // 兼容旧数据：早期矩形可能未持久化 rectangle.constructionLines，只能用端点共享关系推断角点构造线。
        return this.areConstructionLineEndpointsSharedWithCollection(lines, collection);
    }

    /**
     * 解析矩形记录的构造线来源。
     *
     * @param collection 矩形集合对象。
     * @returns 构造线来源；未知时返回 null。
     */
    private resolveConstructionLineMode(collection: SketchCollectionObject): RectangleConstructionLineMode | null {
        const geometryData = collection.geometryData as SketchRectangleConstructionLineGeometryData | null;
        const constructionLines = geometryData?.rectangle?.constructionLines;
        if (constructionLines === RECTANGLE_CONSTRUCTION_LINE_MODE.corners) {
            return RECTANGLE_CONSTRUCTION_LINE_MODE.corners;
        }
        if (constructionLines === RECTANGLE_CONSTRUCTION_LINE_MODE.midpoints) {
            return RECTANGLE_CONSTRUCTION_LINE_MODE.midpoints;
        }
        return null;
    }

    /**
     * 判断构造线端点是否复用矩形集合外框端点。
     *
     * @param lines 构造线对象。
     * @param collection 矩形集合对象。
     * @returns 全部构造线端点都来自外框端点时返回 true。
     */
    private areConstructionLineEndpointsSharedWithCollection(
        lines: readonly SketchLineObject[],
        collection: SketchCollectionObject,
    ): boolean {
        const collectionEndpointIds = this.collectCollectionEndpointIds(collection);
        if (collectionEndpointIds.size === 0) {
            return false;
        }

        return lines.every((line) => {
            return collectionEndpointIds.has(line.startPoint.uuid)
                && collectionEndpointIds.has(line.endPoint.uuid);
        });
    }

    /**
     * 收集矩形集合外框端点 ID。
     *
     * @param collection 矩形集合对象。
     * @returns 外框端点 ID 集合。
     */
    private collectCollectionEndpointIds(collection: SketchCollectionObject): Set<string> {
        const endpointIds = new Set<string>();
        collection.collectSet.forEach((source) => {
            if (!isSketchLineObject(source)) {
                return;
            }
            endpointIds.add(source.startPoint.uuid);
            endpointIds.add(source.endPoint.uuid);
        });
        return endpointIds;
    }

    /**
     * 判断构造线端点是否存在中点几何关系。
     *
     * @param lines 构造线对象。
     * @returns 任意端点带 `Midpoint` 关系时返回 true。
     */
    private hasMidpointRelation(lines: readonly SketchLineObject[]): boolean {
        return lines.some((line) => {
            return line.startPoint.constraints.hasType(SketchRelationKind.Midpoint)
                || line.endPoint.constraints.hasType(SketchRelationKind.Midpoint);
        });
    }

    /**
     * 从集合几何数据中解析矩形构造线 ID。
     *
     * @param collection 集合对象。
     * @returns 构造线 ID 列表。
     */
    private resolveConstructionLineIds(collection: SketchCollectionObject): string[] {
        if (collection.creationSource !== SketchCollectionCreationSource.Rectangle) {
            return [];
        }
        const geometryData = collection.geometryData as SketchRectangleConstructionLineGeometryData | null;
        const lineIds = geometryData?.constructionLines?.lineIds;
        if (!Array.isArray(lineIds)) {
            return [];
        }

        return lineIds.filter((lineId): lineId is string => typeof lineId === 'string' && lineId.length > 0);
    }

    /**
     * 从集合几何数据中解析矩形构造线辅助点 ID。
     *
     * @param collection 集合对象。
     * @returns 构造线辅助点 ID 列表。
     */
    private resolveConstructionPointIds(collection: SketchCollectionObject): string[] {
        if (collection.creationSource !== SketchCollectionCreationSource.Rectangle) {
            return [];
        }
        const geometryData = collection.geometryData as SketchRectangleConstructionLineGeometryData | null;
        const pointIds = geometryData?.constructionLines?.pointIds;
        if (!Array.isArray(pointIds)) {
            return [];
        }

        return pointIds.filter((pointId): pointId is string => typeof pointId === 'string' && pointId.length > 0);
    }

    /**
     * 把源辅助对象 ID 映射为副本运行时 ID。
     *
     * @param sourceIds 源辅助对象 ID 列表。
     * @param objectMap 源对象 UUID 到副本对象的映射。
     * @param predicate 副本对象类型校验函数。
     * @returns 当前复制域内合法副本对象的运行时 ID。
     */
    private remapCopiedObjectIds(
        sourceIds: readonly unknown[] | undefined,
        objectMap: ReadonlyMap<string, ISketchObject>,
        predicate: (value: unknown) => boolean,
    ): string[] {
        if (!Array.isArray(sourceIds)) {
            return [];
        }

        const copiedIdSet = new Set<string>();
        sourceIds.forEach((sourceId) => {
            if (typeof sourceId !== 'string') {
                return;
            }
            const copiedObject = objectMap.get(sourceId);
            if (!copiedObject || !predicate(copiedObject)) {
                return;
            }
            copiedIdSet.add(copiedObject.uuid);
        });
        return Array.from(copiedIdSet);
    }
}
//------ 矩形构造线归属解析 结束------
